// #region Imports
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import WorkIcon from "@mui/icons-material/Work";
import { styled, Theme } from "@mui/material";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import RowActions from "_shared/_views/tables/row-actions";
import RowLink from "_shared/_views/tables/row-link";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import Guid from "_shared/guids/guid";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import IndividualProfile from "marketplace/individuals/profiles/individual-profile";
import {
  type MRT_ColumnFiltersState as ColumnFiltersState,
  createMRTColumnHelper,
  type MRT_TableOptions,
  type MRT_PaginationState as PaginationState,
  type MRT_Row as Row,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  type MRT_SortingState as SortingState,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
} from "material-react-table";
import { ToolbarButtonsContainer } from "permissions/_views/tables/permissions-table-styles";
import {
  MobileToolbarButtonsContainer,
  searchFieldSpacing,
  ScrollableTabs,
  searchTextFieldProps,
  topToolbarFitsThePage,
  ToolbarButtons,
} from "_shared/_views/tables/table-toolbar";
import ChatDialog from "messaging/forums/_views/components/forum-chat-dialog";
import Forum from "messaging/forums/forum";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo } from "react";
import { useLoaderData, useNavigate } from "react-router";
import { useSession } from "users/__session/session-context";
import UserInformation from "users/information/user-information";
import { PageTab } from "users/networking/_views/network-page-tabs";
import {
  useCreateChatForumForNetworkConnectionMutation,
  useGetChatForumForNetworkConnectionQuery,
  useGetUserNetworkConnectionsQuery,
  useRemoveNetworkConnectionMutation,
} from "users/networking/connections/_api/user-network-connection-api";
import startDraftProposalWithVendorTeamLeader from "work/proposals/_views/drafts/start-draft-proposal-with-vendor-team-leader";
// #endregion

// #region Styled Components
const TableTopToolbarContainer = styled("div")(() => ({
  maxWidth: "100%",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  width: "100%",
}));
const LowerTopToolbarContainer = styled("div")(() => ({
  minWidth: 0,
  display: "flex",
  flexDirection: "row",
  alignItems: "left",
  width: "100%",
}));
const TableTitle = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
}));
const RowTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.1em",
  fontWeight: "600",
  marginLeft: theme.spacing(1.5),
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const NameCellContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
}));
// #endregion

const columnHelper = createMRTColumnHelper<UserInformation>();

export default function ContactsTab() {
  const handledRef = React.useRef<string | null>(null);

  // #region State
  const [globalFilter, setGlobalFilter] = React.useState("");
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });

  // #Hooks
  const navigate = useNavigate();
  const confirm = useConfirmDialog();
  const { openDialog } = useDialog();
  const dispatch = useAttorneyHubDispatch();
  const session = useSession();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  // Route
  const routeParams = useLoaderData();
  const routeAction = routeParams?.action as string | undefined;
  const routeId = routeParams?.id as string | undefined;
  const chatUserId = useMemo(
    () => (routeAction === "message" && routeId ? new Guid(routeId) : undefined),
    [routeAction, routeId],
  );

  // #region Queries
  const networkConnectionQuery = useGetUserNetworkConnectionsQuery();
  const [removeConnection, removeConnectionResult] = useRemoveNetworkConnectionMutation();
  const [createChat, createChatResult] = useCreateChatForumForNetworkConnectionMutation();
  const chatQuery = useGetChatForumForNetworkConnectionQuery(chatUserId ? { userId: chatUserId } : skipToken);

  const networkConnections = useMemo(() => {
    if (!networkConnectionQuery.data) return [];
    return UserInformation.fromJSONArray(networkConnectionQuery.data);
  }, [networkConnectionQuery.data]);

  const forum = useMemo(() => {
    if (!chatQuery.data) return null;
    return Forum.fromJSON(chatQuery.data);
  }, [chatQuery.data]);

  const handleSendMessageToContact = useCallback(
    async (userId?: Guid): Promise<void> => {
      try {
        if (!userId) throw new Error("No user id provided");

        const parentPath = new URL(".", globalThis.location.origin + globalThis.location.pathname);

        let activeForum = forum;

        if (!forum) {
          await createChat({ connectionId: userId });
          if (!createChatResult.data) {
            throw new Error("Failed to create chat forum");
          }
          activeForum = Forum.fromJSON(createChatResult.data);
        }

        if (!activeForum) {
          throw new Error("No active forum available");
        }

        openDialog({
          title: "Message",
          contentSxProps: {
            display: "flex",
            overflowX: "hidden",
            paddingBottom: 0,
          },
          MuiProps: {
            fullWidth: true,
            maxWidth: "md",
          },
          component: <ChatDialog forums={[activeForum]} />,
          onClose: () => {
            navigate(parentPath.pathname);
          },
        });
      } catch (error) {
        console.error(error);
        enqueueSnackbar("Chat currently unavailable", { variant: "error" });
      }
    },
    [forum, createChat, createChatResult.data, openDialog, navigate],
  );

  const handleViewProfile = useCallback(
    (individualId?: Guid, data?: UserInformation): void => {
      if (!individualId || !data) return;

      const individualProfile = new IndividualProfile({
        userId: data.id,
        firstName: data.name.firstName ?? "",
        lastName: data.name.lastName ?? "",
        avatarId: data.avatarId,
        legalEntityId: data.legalEntityId,
        email: data.email,
      });

      openDialog({
        titleStyle: {
          paddingBottom: 0,
        },
        contentSxProps: {
          display: "flex",
          overflowX: "hidden",
        },
        MuiProps: {
          maxWidth: "lg",
          fullWidth: false,
        },
        onClose: () => {
          const parentPath = new URL("..", globalThis.location.origin + globalThis.location.pathname);
          navigate(parentPath.pathname);
        },
        component: (
          <ViewIndividualProfile
            individualId={individualId}
            profile={individualProfile}
            proposalTarget={{
              userId: data.id,
              legalEntityId: data.legalEntityId,
              hasSigningAuthority: data.hasSigningAuthority,
            }}
          />
        ),
      });
    },
    [openDialog, navigate],
  );

  // #region Lifecycle
  useEffect(() => {
    // Handle URL changes
    if (!routeId) return;

    const key = `${routeAction}-${routeId}`;
    if (handledRef.current === key) return;

    if (routeAction === "message" && chatUserId) {
      handledRef.current = key;
      void handleSendMessageToContact(chatUserId);
    } else if (routeAction === "view") {
      const match = networkConnections.find((nc) => nc.individualId?.value === routeId);
      if (!match) return;
      handledRef.current = key;
      handleViewProfile(new Guid(routeId), match);
    }
  }, [routeAction, routeId, networkConnections, handleSendMessageToContact, handleViewProfile, chatUserId]);
  // #endregion

  // #region Functions
  const handleRemoveNetworkConnection = useCallback(
    async (connectionId?: Guid): Promise<void> => {
      try {
        if (!connectionId) throw new Error("No network connection id provided");

        const response = await confirm({
          title: "Remove Network Connection",
          message: "Are you sure you want to remove this network connection?",
          okButtonText: "Remove",
        });

        if (response === ConfirmResponse.Cancel) return;

        await removeConnection({ connectionId });

        if (removeConnectionResult.isError) {
          throw removeConnectionResult.error;
        }
      } catch (error) {
        console.error(error);
        enqueueSnackbar("Failed to remove network connection", {
          variant: "error",
        });
      }
    },
    [confirm, removeConnection, removeConnectionResult.isError, removeConnectionResult.error],
  );

  const canCreateProposalFromContact = useCallback((contact: UserInformation): boolean => {
    if (session.user?.id?.isEqualTo(contact.id)) return false;
    return session.user?.legalEntityId?.isEqualTo(contact.legalEntityId) === false;
  }, [session.user?.id, session.user?.legalEntityId]);

  const handleCreateProposalFromContact = useCallback((contact: UserInformation): void => {
    if (!canCreateProposalFromContact(contact)) return;

    startDraftProposalWithVendorTeamLeader({
      dispatch,
      leader: {
        userId: contact.id,
        legalEntityId: contact.legalEntityId,
      },
      navigate,
      session,
    });
  }, [canCreateProposalFromContact, dispatch, navigate, session]);
  // #endregion

  // #region Columns
  const rowActions = useCallback(
    (row: Row<UserInformation>) => {
      const contactName = row.original.name.fullName;
      const canCreateProposal = canCreateProposalFromContact(row.original);
      return (
        <RowActions
          ariaLabel={`Actions for ${contactName}`}
          actions={[
            {
              key: "create-proposal",
              label: "Create Proposal",
              ariaLabel: `Create proposal from ${contactName}`,
              icon: <WorkIcon />,
              disabled: !canCreateProposal,
              disabledReason: "Proposals can only be created with contacts outside your organization",
              onClick: () => handleCreateProposalFromContact(row.original),
            },
            {
              key: "remove-connection",
              label: "Remove Connection",
              ariaLabel: `Remove connection with ${contactName}`,
              icon: <DeleteIcon />,
              destructive: true,
              onClick: () => {
                void handleRemoveNetworkConnection(row.original.connectionId);
              },
            },
          ]}
        />
      );
    },
    [canCreateProposalFromContact, handleCreateProposalFromContact, handleRemoveNetworkConnection],
  );

  const renderContactNameColumn = useCallback(
    (user: UserInformation) => {
      return (
        <NameCellContainer>
          <IndividualAvatar individual={user.toIndividual()} />
          <RowTitle>
            <RowLink to={`/network/contacts/view/${user.individualId}`}>{user.name.fullName}</RowLink>
          </RowTitle>
        </NameCellContainer>
      );
    },
    [],
  );

  const columns = useMemo(
    () => [
      columnHelper.display({
        header: "Name",
        id: "name",
        Cell: (info) => renderContactNameColumn(info.row.original),
      }),
      columnHelper.display({
        header: "Connected On",
        id: "createdDate",
        Cell: (info) => info.row.original.createdDate?.format("L"),
      }),
    ],
    [renderContactNameColumn],
  );
  // #endregion

  // #region Table
  const tableOptions: MRT_TableOptions<UserInformation> = {
    columns: columns,
    data: networkConnections,
    getRowId: (row) => row.connectionId?.value ?? row.id?.value ?? "",
    enableRowSelection: false,
    enableTableHead: true,
    enableRowActions: true,
    enableColumnPinning: true,
    renderRowActions: ({ row }) => rowActions(row),
    renderToolbarInternalActions: ({ table }) =>
      !isMediumDisplaySize && (
        <ToolbarButtonsContainer>
          <ToolbarButtons>
            <GlobalFilterTextField table={table} sx={searchFieldSpacing} />
            <ToggleFiltersButton table={table} />
            <ShowHideColumnsButton table={table} />
            <ToggleFullScreenButton table={table} />
          </ToolbarButtons>
        </ToolbarButtonsContainer>
      ),
    renderTopToolbarCustomActions: ({ table }) => {
      return (
        <TableTopToolbarContainer>
          <TableTitle variant="h4">Network</TableTitle>
          <LowerTopToolbarContainer>
            <ScrollableTabs
              value={PageTab.Contacts}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              onChange={(_event, newValue) => {
                navigate(`/network/${newValue}`);
              }}
            >
              <Tab value={PageTab.Contacts} label="Contacts" />
              <Tab value={PageTab.Coworkers} label="Coworkers" />
              <Tab value={PageTab.Pending} label="Pending" />
              <Tab value={PageTab.Groups} label="Groups" />
            </ScrollableTabs>
          </LowerTopToolbarContainer>
          {isMediumDisplaySize && (
            <MobileToolbarButtonsContainer>
              <GlobalFilterTextField table={table} />
              <ToolbarButtons>
                <ToggleFiltersButton table={table} />
                <ShowHideColumnsButton table={table} />
                <ToggleFullScreenButton table={table} />
              </ToolbarButtons>
            </MobileToolbarButtonsContainer>
          )}
        </TableTopToolbarContainer>
      );
    },
    initialState: {
      showGlobalFilter: true,
      columnPinning: {
        right: ["mrt-row-actions"],
      },
    },
    positionGlobalFilter: "none",
    muiSearchTextFieldProps: ({ table }) =>
      searchTextFieldProps({ searches: "contacts", table }),
    renderEmptyRowsFallback: emptyRowsFallback({
      isError: networkConnectionQuery.isError,
      isLoading: networkConnectionQuery.isFetching,
      onRetry: () => void networkConnectionQuery.refetch(),
      subject: "contacts",
    }),
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    manualPagination: true,
    manualSorting: true,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        navigate(`/network/contacts/view/${row.original.individualId}`);
      },
      sx: { cursor: "pointer" },
    }),
    muiTableContainerProps: {
      sx: {
        flex: 1,
      },
    },
    muiTopToolbarProps: { sx: topToolbarFitsThePage },
    muiTablePaperProps: {
      sx: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
      },
    },
    muiToolbarAlertBannerProps: networkConnectionQuery.isError
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,
    rowCount: networkConnections.length,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    state: {
      globalFilter,
      columnFilters,
      isLoading: networkConnectionQuery.isLoading,
      pagination,
      showAlertBanner: networkConnectionQuery.isError,
      showProgressBars: networkConnectionQuery.isFetching,
      showSkeletons: networkConnectionQuery.isLoading,
      sorting,
    },
  }
  // #endregion

  // #region Render
  return <MRTNoMemo<UserInformation> options={tableOptions} />;
}
